import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { logger } from '../../lib/logger';
import { reportError } from '../../lib/reportError';
import { getNetworkErrors, isGenericError } from '../../lib/error';
import { PaymentSource, CVV_ERROR } from '../../constants';
import { hrefFor } from '../../routers';
import { useGiftCode } from '../../components/GiftCode/useGiftCode';
import { useCheckoutFormStates } from '../../components/CheckoutFormStates';
import { useCart } from '../useCart';
import { useUser } from '../useUser';
import { useAddressesQuery } from '../useAddress';
import { usePaymentMethodsQuery } from '../usePaymentMethods';
import { useErrorModal } from '../useErrorModal';
import { useOrderConvertedTracking } from './useOrderConvertedTracking';
import { usePlaceOrderMutation } from './usePlaceOrderMutation';
import { useAffirmCheckout } from './useAffirmCheckout';
import { usePaymentError } from './usePaymentError';
import { useCheckoutErrorTracking } from './useCheckoutErrorTracking';
import { useVerifyShippingAndPayment } from './useVerifyShippingAndPayment';

export const useCheckout = () => {
  const history = useHistory();
  const { showErrorModal } = useErrorModal();
  const { user } = useUser();
  const { cart } = useCart();
  const { addresses } = useAddressesQuery();
  const { data: paymentMethods } = usePaymentMethodsQuery();
  const { applyGiftCode } = useGiftCode();
  const [placeOrder] = usePlaceOrderMutation();
  const { trackOrderConverted } = useOrderConvertedTracking();
  const { trackCheckoutError } = useCheckoutErrorTracking();
  const { placeAffirmOrder } = useAffirmCheckout();
  const { setPaymentError } = usePaymentError();
  const verifyShippingAndPayment = useVerifyShippingAndPayment();
  const { formStates } = useCheckoutFormStates();

  const isCartValid =
    Boolean(cart.id) && cart.attributes.cartItems.every(item => !item.data.attributes.error);
  const isShippingAddressSelected =
    Boolean(cart.attributes.addressId) &&
    addresses.some(address => address.id === String(cart.attributes.addressId));
  const isShippingMethodSelected = Boolean(cart.attributes.selectedShippingMethodId);
  const isPaymentMethodSelected =
    (Boolean(cart.attributes.paymentMethodId) &&
      paymentMethods?.paymentMethods.data.some(
        paymentMethod => paymentMethod.id === String(cart.attributes.paymentMethodId)
      )) ||
    cart.attributes.paymentSource === PaymentSource.Affirm;
  const hasInvalidCoupon = cart.attributes.error?.type.name === 'Invalid Coupon';
  const areAllFormsClosed =
    formStates.shippingAddress === 'closed' &&
    formStates.shippingMethod === 'closed' &&
    formStates.paymentMethod === 'closed';

  const canPlaceOrder =
    isCartValid &&
    isShippingAddressSelected &&
    isShippingMethodSelected &&
    isPaymentMethodSelected &&
    areAllFormsClosed;

  const handlePlaceOrder = useCallback(async () => {
    if (!canPlaceOrder) return;

    if (hasInvalidCoupon) {
      await applyGiftCode(null);
    }

    try {
      const isShippingAndPaymentUpToDate = await verifyShippingAndPayment();

      if (!isShippingAndPaymentUpToDate) return;

      const { data } =
        cart.attributes.paymentSource === PaymentSource.Affirm
          ? await placeAffirmOrder()
          : await placeOrder();

      trackOrderConverted(data?.checkout.data[0]);

      data &&
        history.push(
          hrefFor.OrderConfirmationPage({
            orderNumber: data.checkout.data[0].attributes.orderNumber
          })
        );
    } catch (error) {
      if (isGenericError(error) && error.reason !== 'canceled') {
        const errorMessage = getNetworkErrors(error)?.[0]?.detail;
        setPaymentError(errorMessage ?? null);

        if (errorMessage !== CVV_ERROR) {
          showErrorModal(errorMessage);
          logger.info('CHECKOUT_FAILED', error.toString(), {
            error: error.toString(),
            message: errorMessage,
            email: user.email,
            guest: !user.isLoggedIn,
            paymentSource: cart.attributes.paymentSource,
            total: cart.attributes.paymentSummary.total.price,
            payToday: cart.attributes.paymentSummary.payToday.price,
            payLater: cart.attributes.paymentSummary.payLater.price
          });
        }

        reportError('PLACE_ORDER', error);
        trackCheckoutError(cart.attributes.paymentSource, error);
      }
    }
  }, [
    canPlaceOrder,
    hasInvalidCoupon,
    applyGiftCode,
    verifyShippingAndPayment,
    cart.attributes.paymentSource,
    cart.attributes.paymentSummary.total.price,
    cart.attributes.paymentSummary.payToday.price,
    cart.attributes.paymentSummary.payLater.price,
    placeAffirmOrder,
    placeOrder,
    trackOrderConverted,
    history,
    setPaymentError,
    trackCheckoutError,
    showErrorModal,
    user.email,
    user.isLoggedIn
  ]);

  return {
    isCartValid,
    isShippingAddressSelected,
    isShippingMethodSelected,
    isPaymentMethodSelected,
    canPlaceOrder,
    handlePlaceOrder
  };
};
